import { StructuredActionTaken } from "@swivl-mono/types";
import type { ChatPrimeRequest } from "@swivl-mono/types";
import { useCallback, useEffect, useRef, useState } from "react";
import { v4 as uuid } from "uuid";

import { playMessageDing } from "../Helpers/messageDing";
import {
  type StreamStatusUpdate,
  advanceUserTicks,
  computeUnreadCount,
  deriveHistoryUserTicks,
  highestKnownIndex,
  ingestMessage,
  ingestStatusUpdates,
  mergeFetchedMessages
} from "../MessageStream/messageStream";
import { BotChatSocketType, createSocket } from "../Socket/BotChatSocket";
import { ChatMessage } from "../Types/BotChat.Types";

const MSG_INDEX_STORAGE_PREFIX = "swivl-bot-chat-msgidx-";
const ACTIVE_STORAGE_PREFIX = "swivl-bot-chat-active-";
const ESCALATED_STORAGE_PREFIX = "swivl-bot-chat-escalated-";
// Highest assistant messageIndex the user has actually SEEN (panel open).
// Distinct from the received-index above — persists across page loads so the
// unread badge survives navigation until they open the chat.
const READ_STORAGE_PREFIX = "swivl-bot-chat-read-";
/** How long after the user was last connected we still auto-reconnect on a
 * fresh page load (so agent messages keep arriving as they browse the site). */
const RECENT_ACTIVE_WINDOW_MS = 45 * 60 * 1000;

/**
 * Whether this org's user should auto-reconnect on a fresh page load even
 * with the chat closed — true if they were connected within the last 45 min, or
 * the conversation is in an active escalation (an agent reply may still land).
 * Lets the widget keep the user present + delivering messages across page
 * navigations within a session. See Phase 1d plan §reconnect.
 */
export function isRecentlyActive(orgId: number): boolean {
  try {
    if (localStorage.getItem(`${ESCALATED_STORAGE_PREFIX}${orgId}`) === "1") return true;
    const last = Number(localStorage.getItem(`${ACTIVE_STORAGE_PREFIX}${orgId}`) || 0);
    return last > 0 && Date.now() - last < RECENT_ACTIVE_WINDOW_MS;
  } catch {
    return false;
  }
}

export interface UseChatResult {
  messages: ChatMessage[];
  isTyping: boolean;
  connected: boolean;
  /** False until the first `history` payload lands. Lets consumers render a
   *  loading skeleton instead of an empty timeline during the initial
   *  socket + conversation-resolution roundtrip. */
  historyLoaded: boolean;
  conversationId: number | null;
  /** Unread assistant messages received while the chat panel was closed. Drives
   *  the launcher unread badge; cleared when the chat opens. */
  unreadCount: number;
  sendMessage: (text: string) => void;
  newConversation: () => void;
  identify: (data: { name?: string; email?: string; phone?: string }) => void;
  /**
   * Ask the bot to rebuild this user's context (verification, focus, prompt)
   * without reconnecting — after the Account tab verifies a phone for the
   * SAME user the socket is already anchored to (ENG-3190). No-op offline.
   */
  refreshContext: () => void;
  /** Structured-message protocol §6.2 — user tapped a structured option. */
  sendStructuredAction: (messageId: string, action: StructuredActionTaken) => void;
  /**
   * Open primed on an already-connected socket (ENG-3166) — e.g. the visitor clicked a CTA
   * button after the widget reconnected on page load. Emits the `prime` event; the server
   * runs the same primed-opening path (with its once-only guard) as a handshake prime.
   */
  requestPrime: (prime: ChatPrimeRequest) => void;
  /** Structured-message protocol §3.5 — widget-side intercept of a text-link click. */
  reportLinkClick: (messageId: string, url: string) => void;
  /**
   * Widget-driven update to a structured message's `payload.data` (filter
   * refresh, zip-entry submit, "Use my location", etc.). Optimistically
   * updates the local message; the server persists and re-broadcasts via
   * `payload-updated`. See widget design doc §A4.6.
   */
  updatePickerData: (messageId: string, data: Record<string, unknown>) => void;
  /**
   * Change-location-from-unit-picker sub-flow (widget design doc §5). Fires
   * when the user picks a new location in the sub-flow overlay. Server
   * applies focus + fetches units + updates the parent unit-picker payload
   * in place; the result arrives via `payload-updated`. No LLM follow-up.
   */
  changeLocationForUnitPicker: (unitPickerMessageId: string, newLocationId: number) => void;
}

function getCurrentUrl(): string {
  try {
    return window.location.href;
  } catch {
    return "";
  }
}

/**
 * True only when the user is genuinely looking at the chat: the tab is
 * visible AND the window is focused. `document.hidden` alone misses a
 * visible-but-unfocused window (e.g. the agent has the CC console in front and
 * the user's widget window is sitting behind) — that would otherwise mark
 * an incoming agent/bot message "seen" when no one is actually looking. The
 * panel-open part is implicit: `useChat` only runs while ChatWindow is mounted,
 * and closing/minimizing the panel unmounts it.
 */
function isViewing(): boolean {
  if (document.hidden) return false;
  return typeof document.hasFocus !== "function" || document.hasFocus();
}

export function useChat(
  server: string,
  token: string | null,
  userId: number | null,
  orgId: number,
  channel?: string,
  isTest?: boolean,
  /**
   * Whether the chat panel is currently open. The socket can now outlive the
   * open panel (it's hoisted to the persistent widget root so a minimized
   * widget stays connected), so "is the user viewing the chat" is no longer
   * implied by the hook running — read-receipts must additionally require the
   * panel be open. Held in a ref so toggling open/closed never re-runs the
   * socket effect (which would reconnect on every minimize). Defaults to `true`
   * for non-widget callers (e.g. the AI Studio test panel) where the chat is
   * always on screen.
   */
  panelOpen: boolean = true,
  /** The v4 widget's publicId — forwarded on the handshake so the bot applies the widget's display rules (ENG-2753). */
  publicId?: string,
  /**
   * Open primed (ENG-3112 / ENG-3166). Sent on the FIRST handshake only: once connected the
   * prime is removed from `socket.auth`, so socket.io's automatic reconnects never re-send
   * it. (The server guards independently against a repeat.)
   */
  prime?: ChatPrimeRequest,
  /**
   * Fired on the bot's `identified` event — after an in-chat phone
   * verification merges the user, the payload carries the canonical chat
   * token so the embedding runtime can re-anchor identity and sync the
   * Account tab (ENG-3190 QA rejection, Bug 2). Held in a ref so a changing
   * callback never re-runs the socket effect.
   */
  onIdentified?: (payload: { userId: number; name?: string; chatToken?: string }) => void
): UseChatResult {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isTyping, setIsTyping] = useState(false);
  const [connected, setConnected] = useState(false);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const [conversationId, setConversationId] = useState<number | null>(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const socketRef = useRef<BotChatSocketType | null>(null);
  // Prime delivery (ENG-3112/3166): the prime present when the socket is CREATED rides the
  // handshake; one that arrives later (a CTA clicked on an already-connected socket) is sent
  // as the `prime` event — queued until `connect` if the socket isn't up yet. Each prime is
  // delivered once; the server guards against repeats independently.
  const deliveredPrimeRef = useRef<ChatPrimeRequest | undefined>(undefined);
  const pendingPrimeRef = useRef<ChatPrimeRequest | undefined>(undefined);
  const lastUrlRef = useRef<string>(getCurrentUrl());
  const panelOpenRef = useRef(panelOpen);
  const onIdentifiedRef = useRef(onIdentified);
  onIdentifiedRef.current = onIdentified;
  // Synchronous source of truth for the message stream. Socket handlers can
  // fire back-to-back within one tick; React state updaters can be deferred
  // (React 18 / StrictMode), so every write goes through `applyMessages`,
  // which updates the ref synchronously and mirrors into state for rendering.
  const messagesRef = useRef<ChatMessage[]>([]);
  const applyMessages = useCallback((update: (prev: ChatMessage[]) => ChatMessage[]): ChatMessage[] => {
    const next = update(messagesRef.current);
    messagesRef.current = next;
    setMessages(next);
    return next;
  }, []);
  // message-status updates that raced ahead of their message (R8) — re-applied
  // after the next message/history ingest.
  const bufferedStatusRef = useRef<StreamStatusUpdate[]>([]);

  const msgIndexKey = `${MSG_INDEX_STORAGE_PREFIX}${orgId}`;
  const activeKey = `${ACTIVE_STORAGE_PREFIX}${orgId}`;
  const escKey = `${ESCALATED_STORAGE_PREFIX}${orgId}`;
  const readKey = `${READ_STORAGE_PREFIX}${orgId}`;

  // Keep the panel-open ref current without re-running the socket effect. When
  // the panel opens (connected + tab viewed), the user is now looking at
  // the chat → mark the recipient's messages seen.
  useEffect(() => {
    panelOpenRef.current = panelOpen;
    // Tell the bot whether the user is actively viewing the chat, so the CC
    // can show "chatting" vs "connected but chat closed".
    if (socketRef.current?.connected) socketRef.current.emit("viewing", { viewing: panelOpen });
    if (panelOpen) {
      // Opening the chat = everything received so far is now read → clear the
      // unread badge and advance the read marker to the highest received seq.
      setUnreadCount(0);
      try {
        const received = localStorage.getItem(msgIndexKey);
        if (received) localStorage.setItem(readKey, received);
      } catch {
        /* ignore */
      }
      if (socketRef.current?.connected && isViewing()) socketRef.current.emit("read");
    }
  }, [panelOpen, msgIndexKey, readKey]);

  // Stamp "active now" so a later page load knows to auto-reconnect (see isRecentlyActive).
  const markActive = useCallback((): void => {
    try {
      localStorage.setItem(activeKey, String(Date.now()));
    } catch {
      /* ignore */
    }
  }, [activeKey]);

  useEffect(() => {
    if (!token || !userId) return;

    const socket = createSocket(server, token, userId, orgId, getCurrentUrl(), channel, isTest, publicId, prime);
    socketRef.current = socket;
    if (prime) deliveredPrimeRef.current = prime;

    // R8: re-apply status updates that raced ahead of their message, after any
    // new messages land (live push or history merge).
    const drainBufferedStatus = (): void => {
      if (bufferedStatusRef.current.length === 0) return;
      const { messages: next, unmatched } = ingestStatusUpdates(messagesRef.current, bufferedStatusRef.current);
      bufferedStatusRef.current = unmatched;
      if (next !== messagesRef.current) applyMessages(() => next);
    };

    socket.on("connect", () => {
      setConnected(true);
      markActive();
      // The prime is consumed by this connection — drop it so reconnects don't re-prime.
      if (typeof socket.auth === "object" && (socket.auth as Record<string, unknown>)["prime"]) {
        socket.auth = { ...(socket.auth as Record<string, unknown>), prime: undefined };
      }
      // A prime that arrived while the socket was still connecting.
      if (pendingPrimeRef.current) {
        socket.emit("prime", { prime: pendingPrimeRef.current });
        deliveredPrimeRef.current = pendingPrimeRef.current;
        pendingPrimeRef.current = undefined;
      }
      // Report initial viewing state (chat may connect open or closed).
      socket.emit("viewing", { viewing: panelOpenRef.current });
    });

    socket.on("disconnect", () => {
      setConnected(false);
    });

    socket.on("history", (payload) => {
      // MERGE into the current stream (never replace): the server pushes full
      // history on every (re)connect, and the R2 gap fetch requests a delta —
      // the shared core dedups either shape by messageId/messageIndex and
      // reconciles any still-pending optimistic send (ENG-2825). The ref is
      // the synchronous authority so back-to-back socket events never merge
      // against stale state.
      applyMessages((prev) => deriveHistoryUserTicks(mergeFetchedMessages(prev, payload.messages).messages));
      drainBufferedStatus();
      setConversationId(payload.conversationId);
      setHistoryLoaded(true);
      // Update last known messageIndex + reconcile the unread badge. On a closed-chat
      // reconnect (browsing across pages), unread = assistant messages newer than
      // the last messageIndex the user actually read; if the panel is open they're
      // reading now, so mark all read.
      if (payload.messages.length > 0) {
        const maxIndex = Math.max(...payload.messages.map((m) => m.messageIndex));
        localStorage.setItem(msgIndexKey, String(maxIndex));
        if (panelOpenRef.current) {
          localStorage.setItem(readKey, String(maxIndex));
          setUnreadCount(0);
        } else {
          const lastRead = Number(localStorage.getItem(readKey) || "0");
          setUnreadCount(computeUnreadCount(payload.messages, lastRead));
        }
      }
      // Read receipt for the AGENT side (stamps seenBy.user on the bot's
      // messages) — only when the user is actually viewing (panel open +
      // tab visible + window focused). Does not drive the widget's own ticks
      // (those live on the user's messages).
      if (panelOpenRef.current && isViewing() && payload.messages.length > 0) socket.emit("read");
    });

    socket.on("message", (payload) => {
      // Ingest through the shared core: dedup by messageId/messageIndex, the
      // echo of our own send reconciles its optimistic copy, and a skipped-
      // ahead messageIndex means something was lost in transit — request the
      // delta replay (R2; the server's history handler supports it). The ref
      // is the synchronous authority so back-to-back socket events never
      // ingest against stale state.
      const { messages: next, added, gapDetected } = ingestMessage(messagesRef.current, payload);
      if (gapDetected) {
        // Delta cursor is THREAD-scoped (ENG-3036): ask from the incoming
        // thread's own highest index — the localStorage cursor may belong to
        // an earlier conversation whose indexes ran higher.
        socket.emit("history", { lastMessageIndex: highestKnownIndex(messagesRef.current, payload.botConversationId) });
      }
      if (next === messagesRef.current) return;
      if (!added) {
        applyMessages(() => next);
        return;
      }

      // The reply has arrived — typing is over. Clear the indicator now rather
      // than waiting on a separate `typing:false` event, which can land after
      // the message and leave the indicator lingering.
      setIsTyping(false);

      // A bot/agent reply means the recipient has now seen the user's
      // outbound messages — advance their ticks to "seen".
      applyMessages(() => (payload.role === "assistant" ? advanceUserTicks(next, "seen") : next));
      drainBufferedStatus();

      // Stamp activity so a later page load auto-reconnects; flag an active
      // escalation (agent/takeover message) so we reconnect even past the
      // 45-min window while a human is handling the chat.
      markActive();
      if (payload.author === "agent" || payload.author === "system") {
        try {
          localStorage.setItem(escKey, "1");
        } catch {
          /* ignore */
        }
      }

      // A human agent's message gets a ding (ENG-2946) — agent only, never
      // the bot's replies or system notices, whether the panel is open or
      // it's bumping the unread badge.
      if (payload.author === "agent") playMessageDing();

      // ACK immediately
      socket.emit("ack", { messageId: payload.messageId });

      // User read receipt (agent side) — only when the user is actually
      // viewing (panel open + tab visible + window focused).
      if (panelOpenRef.current && isViewing()) socket.emit("read");

      // Update last known messageIndex
      localStorage.setItem(msgIndexKey, String(payload.messageIndex));

      // Unread badge: an assistant (bot/agent) message that arrives while the
      // chat is closed bumps the launcher count; if the panel's open it's read
      // immediately, so just advance the read marker.
      if (payload.role === "assistant") {
        if (panelOpenRef.current) {
          try {
            localStorage.setItem(readKey, String(payload.messageIndex));
          } catch {
            /* ignore */
          }
        } else {
          setUnreadCount((c) => c + 1);
        }
      }
    });

    socket.on("typing", (payload) => {
      setIsTyping(payload.isTyping);
      // Server is handling the turn → the user's pending messages are delivered.
      if (payload.isTyping) applyMessages((prev) => advanceUserTicks(prev, "delivered"));
    });

    socket.on("error", (payload) => {
      console.error("Bot chat error:", payload.code, payload.message);
    });

    socket.on("identified", (payload) => {
      onIdentifiedRef.current?.(payload);
    });

    // Structured-message protocol §6.2 — server confirmed an action was
    // recorded. Update the message's actionTaken so the picker re-renders
    // in selected state. If the local message had a pending optimistic
    // actionTaken it stays consistent with the server-broadcast value.
    // ENG-3363 — a call-script step sent the customer to one of our pages. The URL carries
    // `swivlOpen=true`, so the chat reopens on arrival when the page embeds the widget.
    socket.on("navigate", (payload) => {
      if (payload?.url) window.location.assign(payload.url);
    });

    socket.on("message-updated", (payload) => {
      applyMessages((prev) => prev.map((m) => (m.messageId === payload.messageId ? { ...m, actionTaken: payload.actionTaken } : m)));
    });

    // Structured-message protocol §3.1 — server rejected the action.
    // Roll back the optimistic actionTaken on the affected message.
    socket.on("action-rejected", (payload) => {
      console.warn("action-rejected:", payload.messageId, payload.reason);
      applyMessages((prev) => prev.map((m) => (m.messageId === payload.messageId ? { ...m, actionTaken: undefined } : m)));
    });

    // Delivery/seen update pushed over the conv room. Applied through the
    // shared core; an update that raced ahead of its message is buffered and
    // re-applied when the message lands (R8) instead of being dropped. This is
    // what advances the user's ticks to "seen" when an agent reads the
    // conversation (seenBy.members) — independent of any reply.
    socket.on("message-status", (payload) => {
      if (!payload?.updates?.length) return;
      // Stamp the envelope's conversation onto each update so the index match
      // stays thread-scoped (ENG-3036).
      const stamped = (payload.updates as StreamStatusUpdate[]).map((u) => ({
        ...u,
        botConversationId: u.botConversationId ?? payload.conversationId
      }));
      const all = [...bufferedStatusRef.current, ...stamped];
      const { messages: next, unmatched } = ingestStatusUpdates(messagesRef.current, all);
      bufferedStatusRef.current = unmatched;
      if (next !== messagesRef.current) applyMessages(() => next);
    });

    // Widget design doc §A4.6 — server broadcast confirming a `payload.data`
    // update (filter refresh, zip-entry submit, etc.). The widget already
    // applied the change optimistically; this re-syncs the local state from
    // the authoritative server value (and lets agent-timeline watchers
    // also see the new state).
    socket.on("payload-updated", (payload) => {
      applyMessages((prev) =>
        prev.map((m) => (m.messageId === payload.messageId && m.payload ? { ...m, payload: { ...m.payload, data: payload.data } } : m))
      );
    });

    // Track URL changes (SPA navigation, pushState, popstate)
    const checkUrl = (): void => {
      const currentUrl = getCurrentUrl();
      if (currentUrl && currentUrl !== lastUrlRef.current) {
        lastUrlRef.current = currentUrl;
        if (socket.connected) {
          socket.emit("page-navigation", { url: currentUrl });
        }
      }
    };

    // Listen for popstate (back/forward) and poll for pushState changes
    window.addEventListener("popstate", checkUrl);
    const urlPollInterval = setInterval(checkUrl, 1000);

    // User read receipt (agent side) when the user comes back to the
    // chat — tab regains visibility (visibilitychange) OR the window regains
    // focus (focus). Both are needed: switching browser tabs fires
    // visibilitychange; switching between separate windows/apps fires
    // focus/blur without changing document.hidden.
    const handleViewingChange = (): void => {
      if (panelOpenRef.current && isViewing() && socket.connected) socket.emit("read");
    };
    document.addEventListener("visibilitychange", handleViewingChange);
    window.addEventListener("focus", handleViewingChange);

    return () => {
      window.removeEventListener("popstate", checkUrl);
      document.removeEventListener("visibilitychange", handleViewingChange);
      window.removeEventListener("focus", handleViewingChange);
      clearInterval(urlPollInterval);
      socket.disconnect();
      socketRef.current = null;
    };
  }, [server, token, msgIndexKey]);

  const sendMessage = useCallback(
    (text: string) => {
      if (!socketRef.current?.connected || !text.trim()) return;

      const messageId = uuid();
      const userMessage: ChatMessage = {
        messageId,
        messageIndex: -1, // Will be assigned by server
        role: "user",
        content: text.trim(),
        createdAt: new Date().toISOString(),
        deliveryStatus: "sent" // tick: sent → delivered (typing) → seen (reply)
      };

      // Optimistic add
      applyMessages((prev) => [...prev, userMessage]);

      socketRef.current.emit("message", { text: text.trim(), messageId, url: getCurrentUrl() });
      markActive();
    },
    [markActive]
  );

  const requestPrime = useCallback((request: ChatPrimeRequest) => {
    const socket = socketRef.current;
    if (socket?.connected) {
      socket.emit("prime", { prime: request });
      deliveredPrimeRef.current = request;
      return;
    }
    pendingPrimeRef.current = request;
  }, []);

  // A `prime` prop that changes after the socket exists (CTA click after reconnect-on-load).
  useEffect(() => {
    if (!prime || prime === deliveredPrimeRef.current) return;
    if (!socketRef.current) return; // the socket effect will put it on the handshake
    requestPrime(prime);
  }, [prime, requestPrime]);

  const newConversation = useCallback(() => {
    if (!socketRef.current?.connected) return;
    socketRef.current.emit("new-conversation");
    applyMessages(() => []);
    localStorage.removeItem(msgIndexKey);
    // Fresh conversation → no longer in the prior escalation.
    try {
      localStorage.removeItem(escKey);
    } catch {
      /* ignore */
    }
  }, [msgIndexKey, escKey]);

  const identify = useCallback((data: { name?: string; email?: string; phone?: string }) => {
    if (!socketRef.current?.connected) return;
    socketRef.current.emit("identify", data);
  }, []);

  const refreshContext = useCallback(() => {
    if (!socketRef.current?.connected) return;
    socketRef.current.emit("refresh-context");
  }, []);

  /**
   * Structured-message protocol §6.2 — user interacted with a structured
   * message. Optimistically updates the local UI to selected state, fires
   * the action-taken socket event. On server ACK, the message-updated
   * listener confirms; on rejection, the action-rejected listener rolls
   * back. If the message has onSelect.kind === "send-message", the
   * configured follow-up user message is auto-sent (per protocol §4).
   */
  const sendStructuredAction = useCallback((messageId: string, action: StructuredActionTaken) => {
    if (!socketRef.current?.connected) return;
    // Optimistic update — render selected state immediately on the structured
    // message itself. NO user-message bubble is dispatched: the structured
    // message's selected state IS the visual record of the pick. The server
    // synthesizes the LLM-facing follow-up at next-turn time from
    // actionTaken (per buttons-plan §2 + protocol §4 unification).
    applyMessages((prev) =>
      prev.map((m) => {
        if (m.messageId !== messageId) return m;
        // A location-picker "selected" tap is "View Units" navigation, not a
        // terminal pick — don't optimistically collapse the carousel to the
        // chosen location. The server keeps it a browsable carousel when the
        // location has units (so the unit-picker's down-caret returns to it),
        // or confirms a selected state via `message-updated` when it doesn't.
        const kind = (m.payload as { kind?: string } | undefined)?.kind;
        if (kind === "location-picker" && action.type === "selected") return m;
        return { ...m, actionTaken: action };
      })
    );
    socketRef.current.emit("action-taken", { messageId, action });
  }, []);

  /**
   * Structured-message protocol §3.5 — widget-side intercept of a text-link
   * click. Fires the action-taken event with a click payload; the server
   * appends to the message's actionTaken array.
   */
  const reportLinkClick = useCallback((messageId: string, url: string) => {
    if (!socketRef.current?.connected) return;
    socketRef.current.emit("action-taken", {
      messageId,
      click: { url, clickedAt: new Date().toISOString(), source: "widget" }
    });
  }, []);

  /**
   * Widget design doc §A4.6 — push a new `payload.data` for a structured
   * message after a filter refresh / zip-entry submit / "Use my location" /
   * etc. Optimistically updates the local message and emits the
   * `picker-data-updated` socket event so the server can persist + re-broadcast.
   */
  const updatePickerData = useCallback((messageId: string, data: Record<string, unknown>) => {
    if (!socketRef.current?.connected) return;
    applyMessages((prev) => prev.map((m) => (m.messageId === messageId && m.payload ? { ...m, payload: { ...m.payload, data } } : m)));
    socketRef.current.emit("picker-data-updated", { messageId, data });
  }, []);

  /**
   * Widget design doc §5 — fires when the user picks a new location in the
   * change-location sub-flow. The server validates ownership, applies focus
   * update, fetches units, persists the new payload, and broadcasts via
   * `payload-updated` (handled by the existing listener above). The widget
   * pops its sub-flow overlay off the takeover stack as soon as this is
   * called — the unit-picker re-renders with new data when the broadcast
   * lands.
   */
  const changeLocationForUnitPicker = useCallback((unitPickerMessageId: string, newLocationId: number) => {
    if (!socketRef.current?.connected) return;
    socketRef.current.emit("subflow-location-changed", { unitPickerMessageId, newLocationId });
  }, []);

  return {
    messages,
    isTyping,
    connected,
    historyLoaded,
    conversationId,
    unreadCount,
    sendMessage,
    newConversation,
    requestPrime,
    identify,
    refreshContext,
    sendStructuredAction,
    reportLinkClick,
    updatePickerData,
    changeLocationForUnitPicker
  };
}
